import type { Patient } from '@medplum/fhirtypes';
import React, { useMemo } from 'react';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Skeleton,
  Stack,
  Text,
} from '@eluve/components';
import { calculateAge } from '@eluve/date-utils';
import {
  FhirResourceScope,
  Field,
  INDIVIDUAL_PRONOUNS_URL,
  US_CORE_ETHNICITY_URL,
  US_CORE_GENDER_IDENTITY_URL,
  US_CORE_RACE_URL,
  buildResolutionMap,
  formatFhirDate,
  formatHumanName,
  patientMrnIdentifierIndex,
  preferredLanguageIndex,
} from '@eluve/fhir-renderer';

import { RailSection } from './RailSection';
import {
  PATIENT_STATUS_LABELS,
  type PatientStatusDimension,
  formatVisitDate,
  hasContactDetails,
  hasIdentityDemographics,
  patientStatus,
  phoneLabel,
  statusMenuLabel,
} from './railPatient';

export type RailFhirState =
  | { status: 'loading' | 'unavailable' | 'error' }
  | { status: 'data'; patient: Patient };

export type RailVisits = {
  lastVisit?: string | null;
  nextVisit?: string | null;
};

export type PatientProfileRailViewProps = {
  identity: { name: string; initials: string; photoUrl?: string | null };
  fhir: RailFhirState;
  /** Resolved GP display name (sync writes references without display). */
  primaryCareName?: string | null;
  visits?: RailVisits;
  /** Enables the Edit profile action; absent while the patient is loading. */
  onEditProfile?: () => void;
  /** Enables the status menu actions; absent while the patient is loading. */
  onStatusAction?: (dimension: PatientStatusDimension) => void;
};

/** Non-FHIR key-value row; visually matches Field's row layout. */
const RailRow: React.FC<React.PropsWithChildren<{ label: string }>> = ({
  label,
  children,
}) => (
  <div className="flex min-w-0 items-baseline justify-between gap-4">
    <Text size="sm" weight="regular" color="tertiary" className="shrink-0">
      {label}
    </Text>
    <Text size="sm" weight="regular" className="min-w-0 text-right">
      {children}
    </Text>
  </div>
);

const RailActionBar: React.FC<{
  onEditProfile?: () => void;
  patient?: Patient;
  onStatusAction?: (dimension: PatientStatusDimension) => void;
}> = ({ onEditProfile, patient, onStatusAction }) => {
  const statusItem = (dimension: PatientStatusDimension, fallback: string) => (
    <DropdownMenuItem
      disabled={!patient || !onStatusAction}
      onSelect={() => onStatusAction?.(dimension)}
    >
      {patient ? statusMenuLabel(patient, dimension) : fallback}
    </DropdownMenuItem>
  );
  return (
    <div className="flex">
      <Button
        variant="outline"
        size="sm"
        disabled={!onEditProfile}
        onClick={onEditProfile}
        className="flex-1 rounded-r-none focus-visible:z-10"
      >
        <Icon name="Pencil" size="auto" />
        Edit profile
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label="More actions"
            className="-ml-px rounded-l-none focus-visible:z-10"
          >
            <Icon name="ChevronDown" size="auto" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {statusItem('deceased', 'Mark as deceased…')}
          {statusItem('left-practice', 'Mark as left the practice…')}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const PatientInformationSection: React.FC<{
  patient: Patient;
  primaryCareName?: string | null;
  visits?: RailVisits;
}> = ({ patient, primaryCareName, visits }) => (
  <RailSection title="Patient information" defaultOpen>
    <RailRow label="Status">
      {PATIENT_STATUS_LABELS[patientStatus(patient)]}
    </RailRow>
    <Field
      label="Date of birth"
      data={patient}
      path={['birthDate']}
      layout="row"
      visibility="when-present-or-withheld"
      render={(value) => {
        const age = value ? calculateAge(value) : undefined;
        const date = formatFhirDate(value);
        return age !== undefined && age >= 0 ? `${date} · ${age} yrs` : date;
      }}
    />
    <Field
      label="MRN"
      data={patient}
      path={['identifier', patientMrnIdentifierIndex(patient), 'value']}
      layout="row"
      visibility="when-present-or-withheld"
      render={(value) => <span className="tabular-nums">{value}</span>}
    />
    {primaryCareName && (
      <RailRow label="Primary care">{primaryCareName}</RailRow>
    )}
    {visits?.lastVisit && (
      <RailRow label="Last visit">{formatVisitDate(visits.lastVisit)}</RailRow>
    )}
    {visits?.nextVisit && (
      <RailRow label="Next visit">{formatVisitDate(visits.nextVisit)}</RailRow>
    )}
  </RailSection>
);

const IdentityDemographicsSection: React.FC<{ patient: Patient }> = ({
  patient,
}) => {
  if (!hasIdentityDemographics(patient)) return null;
  return (
    <RailSection title="Identity & demographics">
      <Field
        label="Legal sex"
        data={patient}
        path={['gender']}
        layout="row"
        visibility="when-present-or-withheld"
        render={(value) =>
          value ? value.charAt(0).toUpperCase() + value.slice(1) : value
        }
      />
      <Field
        label="Gender identity"
        data={patient}
        path={[
          'extension',
          { url: US_CORE_GENDER_IDENTITY_URL },
          'valueCodeableConcept',
        ]}
        layout="row"
        visibility="when-present-or-withheld"
      />
      <Field
        label="Pronouns"
        data={patient}
        path={[
          'extension',
          { url: INDIVIDUAL_PRONOUNS_URL },
          'valueCodeableConcept',
        ]}
        layout="row"
        visibility="when-present-or-withheld"
      />
      <Field
        label="Race"
        data={patient}
        path={[
          'extension',
          { url: US_CORE_RACE_URL },
          'extension',
          { url: 'text' },
          'valueString',
        ]}
        layout="row"
        visibility="when-present-or-withheld"
      />
      <Field
        label="Ethnicity"
        data={patient}
        path={[
          'extension',
          { url: US_CORE_ETHNICITY_URL },
          'extension',
          { url: 'text' },
          'valueString',
        ]}
        layout="row"
        visibility="when-present-or-withheld"
      />
      <Field
        label="Preferred language"
        data={patient}
        path={['communication', preferredLanguageIndex(patient), 'language']}
        layout="row"
        visibility="when-present-or-withheld"
      />
      <Field
        label="Marital status"
        data={patient}
        path={['maritalStatus']}
        layout="row"
        visibility="when-present-or-withheld"
      />
    </RailSection>
  );
};

const ContactRelationshipsSection: React.FC<{ patient: Patient }> = ({
  patient,
}) => {
  if (!hasContactDetails(patient)) return null;
  return (
    <RailSection title="Contact & relationships">
      {(patient.telecom ?? []).map((telecom, index) => {
        if (telecom.system !== 'phone' && telecom.system !== 'email') {
          return null;
        }
        return (
          <Field
            key={index}
            label={
              telecom.system === 'email' ? 'Email' : phoneLabel(telecom.use)
            }
            data={patient}
            path={['telecom', index, 'value']}
            layout="row"
            visibility="when-present-or-withheld"
            render={(value) =>
              telecom.system === 'phone' ? (
                <span className="tabular-nums">{value}</span>
              ) : (
                <span className="break-all">{value}</span>
              )
            }
          />
        );
      })}
      {(patient.address ?? []).map((_, index) => (
        <Field
          key={index}
          label="Address"
          data={patient}
          path={['address', index]}
          layout="row"
          visibility="when-present-or-withheld"
          render={(address) => (
            <span className="flex flex-col">
              {address?.text ? (
                <span>{address.text}</span>
              ) : (
                <>
                  {address?.line?.length ? (
                    <span>{address.line.join(' ')}</span>
                  ) : null}
                  <span>
                    {[
                      [address?.city, address?.state]
                        .filter(Boolean)
                        .join(', '),
                      address?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </span>
                </>
              )}
            </span>
          )}
        />
      ))}
      {(patient.contact ?? []).map((_, index) => (
        <Field
          key={index}
          label="Emergency contact"
          data={patient}
          path={['contact', index]}
          layout="row"
          visibility="when-present-or-withheld"
          render={(contact) => {
            const phone = contact?.telecom?.find(
              (telecom) => telecom.system === 'phone',
            )?.value;
            return (
              <span className="flex flex-col gap-0.5">
                {contact?.name && <span>{formatHumanName(contact.name)}</span>}
                {contact?.relationship?.[0] && (
                  <span>
                    {contact.relationship[0].text ??
                      contact.relationship[0].coding?.[0]?.display}
                  </span>
                )}
                {phone && <span className="tabular-nums">{phone}</span>}
              </span>
            );
          }}
        />
      ))}
    </RailSection>
  );
};

const RailFhirSections: React.FC<{
  patient: Patient;
  primaryCareName?: string | null;
  visits?: RailVisits;
}> = ({ patient, primaryCareName, visits }) => {
  const map = useMemo(() => buildResolutionMap([patient]), [patient]);
  return (
    <FhirResourceScope map={map} placement="side-panel">
      <PatientInformationSection
        patient={patient}
        primaryCareName={primaryCareName}
        visits={visits}
      />
      <IdentityDemographicsSection patient={patient} />
      <ContactRelationshipsSection patient={patient} />
    </FhirResourceScope>
  );
};

const RailSkeleton: React.FC = () => (
  <Stack gap={3} align="stretch" className="py-3">
    {Array.from({ length: 5 }, (_, index) => (
      <Skeleton key={index} className="h-4 w-full" />
    ))}
  </Stack>
);

const RailNotice: React.FC<{ message: string }> = ({ message }) => (
  <Stack gap={2} align="center" className="py-6 text-center">
    <Icon name="Lock" className="text-text-tertiary" />
    <Text size="sm" color="tertiary">
      {message}
    </Text>
  </Stack>
);

export const PatientProfileRailView: React.FC<PatientProfileRailViewProps> = ({
  identity,
  fhir,
  primaryCareName,
  visits,
  onEditProfile,
  onStatusAction,
}) => {
  const status = fhir.status === 'data' ? patientStatus(fhir.patient) : null;

  return (
    <Stack gap={4} align="stretch">
      <Stack
        direction="horizontal"
        gap={3}
        fullWidth={false}
        className="items-center"
      >
        {identity.photoUrl ? (
          <img
            src={identity.photoUrl}
            alt=""
            className="size-9 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold privacy-text">
            {identity.initials}
          </span>
        )}
        <Stack gap={0} className="min-w-0">
          <Text size="md" weight="semibold" className="privacy-text">
            {identity.name}
          </Text>
          {status && status !== 'active' && (
            <Text size="xs" color="tertiary">
              {PATIENT_STATUS_LABELS[status]}
            </Text>
          )}
        </Stack>
      </Stack>
      <RailActionBar
        onEditProfile={onEditProfile}
        patient={fhir.status === 'data' ? fhir.patient : undefined}
        onStatusAction={onStatusAction}
      />
      <div>
        {fhir.status === 'loading' && <RailSkeleton />}
        {fhir.status === 'unavailable' && (
          <RailNotice message="Patient record unavailable or restricted." />
        )}
        {fhir.status === 'error' && (
          <RailNotice message="Couldn't load the patient record." />
        )}
        {fhir.status === 'data' && (
          <RailFhirSections
            patient={fhir.patient}
            primaryCareName={primaryCareName}
            visits={visits}
          />
        )}
      </div>
    </Stack>
  );
};
